import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComment,
  faEye,
  faHeart,
  faShare,
  faExternalLinkAlt,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { SocialWallPost } from "../../../cores/socialWall/interfaces";
import { socialWallApiService } from "../../../cores/socialWall/services/api";
import { PROVIDER_META } from "./boardStorage";
import { SocialWallDeleteModal } from "./SocialWallDeleteModal";

function formatRelative(dateStr: string | null) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  return d.toLocaleDateString();
}

function formatCount(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n || 0);
}

interface Props {
  post: SocialWallPost;
  companyId: string;
  compact?: boolean;
  onDeleted?: (postId: string) => void;
}

export function SocialWallPostCard({
  post,
  companyId,
  compact,
  onDeleted,
}: Props) {
  const intl = useIntl();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const meta = PROVIDER_META[post.provider] || {
    label: post.provider,
    color: "#64748b",
  };
  const thumb = useMemo(() => {
    const m = post.media?.[0];
    return m?.thumbnailUrl || m?.url || "";
  }, [post.media]);
  const title =
    (post.raw as { title?: string } | null)?.title ||
    (post.media?.[0] as { title?: string } | undefined)?.title ||
    "";
  const canDelete = Boolean(post.canDelete);

  const runDelete = async () => {
    if (!canDelete || deleting) return;
    setDeleting(true);
    try {
      await socialWallApiService.deletePost(companyId, {
        provider: post.provider,
        postId: post.id,
      });
      toast.success(
        intl.formatMessage({ id: "app.socialWall.deletePostSuccess" })
      );
      setConfirmOpen(false);
      onDeleted?.(post.id);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ||
        (e as { message?: string })?.message ||
        intl.formatMessage({ id: "app.socialWall.deletePostError" });
      toast.error(String(msg));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <article className={`sw-card${compact ? " sw-card--compact" : ""}`}>
        <header className="sw-card__head">
          <div className="sw-card__avatar">
            {post.accountAvatar ? (
              <img src={post.accountAvatar} alt="" />
            ) : (
              <span style={{ background: meta.color }} />
            )}
          </div>
          <div className="sw-card__meta">
            <div className="sw-card__name">{post.accountName || meta.label}</div>
            <div className="sw-card__time">{formatRelative(post.createdAt)}</div>
          </div>
          <div className="sw-card__actions">
            {canDelete ? (
              <button
                type="button"
                className="sw-card__delete"
                onClick={() => setConfirmOpen(true)}
                disabled={deleting}
                title={intl.formatMessage({ id: "app.socialWall.deletePost" })}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </button>
            ) : null}
            {post.permalink ? (
              <a
                className="sw-card__open"
                href={post.permalink}
                target="_blank"
                rel="noreferrer"
                title="Ver en la red"
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </a>
            ) : null}
          </div>
        </header>

        {title ? <h4 className="sw-card__title">{title}</h4> : null}

        {post.text ? (
          <p className="sw-card__text">
            {post.text.length > 280 ? `${post.text.slice(0, 280)}…` : post.text}
          </p>
        ) : null}

        {thumb ? (
          <div className="sw-card__media">
            <img src={thumb} alt="" />
            {post.mediaType === "video" ? (
              <span className="sw-card__play">▶</span>
            ) : null}
          </div>
        ) : null}

        <footer className="sw-card__stats">
          <span title="Likes">
            <FontAwesomeIcon icon={faHeart} /> {formatCount(post.metrics?.likes)}
          </span>
          <span title="Comentarios">
            <FontAwesomeIcon icon={faComment} />{" "}
            {formatCount(post.metrics?.comments)}
          </span>
          {(post.metrics?.views || 0) > 0 ? (
            <span title="Vistas">
              <FontAwesomeIcon icon={faEye} /> {formatCount(post.metrics.views)}
            </span>
          ) : null}
          {(post.metrics?.shares || 0) > 0 ? (
            <span title="Shares">
              <FontAwesomeIcon icon={faShare} />{" "}
              {formatCount(post.metrics.shares)}
            </span>
          ) : null}
        </footer>
      </article>

      <SocialWallDeleteModal
        isOpen={confirmOpen}
        deleting={deleting}
        accountName={post.accountName || meta.label}
        previewText={post.text || title}
        onCancel={() => !deleting && setConfirmOpen(false)}
        onConfirm={() => void runDelete()}
      />
    </>
  );
}
